import { TFabricDefectCategories, TStockType } from '@monorepo/types';
import { TBucketInfo } from 'libs/services/src/lib/capacitor/bucket-storage.types';

export type TFilterFabricInspectionType =
  | 'DATE'
  | 'LOT_STATUS'
  | 'BUYER'
  | 'PLM'
  | 'PO'
  | 'STYLE'
  | 'COLOR'
  | 'SUPPLIER_NAME'
  | 'LOT_NO'
  | 'FABRIC_VARIANT'
  | 'FABRIC_TYPE'
  | 'MATERIAL_CODE'
  | 'ITEM_NAMES'
  | 'GRN_STATUS'
  | 'PACKED';

export type TFilterWMSFabricInspectionType =
  | TFilterFabricInspectionType
  | 'NAME'
  | 'ITEM_ID'
  | 'STOCK_TYPE'
  | 'ITEM_TYPE'
  | 'UOM';

export type TFilterFabricInspection = {
  filterType: TFilterFabricInspectionType;
  values: (string | number)[];
};

export type TFilterWMSFabricInspection = {
  displayKey?: string;
  filterType: TFilterWMSFabricInspectionType;
  values: (string | number)[];
};

export type TransactionRollListingType = 'COMMERCIALLY_APPROVE' | 'DEFAULT';
export type RollFilterType = 'ROLL_NO' | 'STATUS' | 'GRN_STATUS' | 'GRN_NUMBER';
export const RollFilterTypeToDSMap: Record<RollFilterType, keyof TRollInfo> = {
  ROLL_NO: 'rollNo',
  STATUS: 'status',
  GRN_STATUS: 'grnStatus',
  GRN_NUMBER: 'grnNumber',
};
export type TFabricRollFilter = {
  filterType: RollFilterType;
  values: (string | number)[];
};

export type TAppDetails = {
  factoryId: string;
  appVersion: string;
  shift: string;
  subject: string;
  deviceId: string;
  urId: string;
  urNm: string;
};

export type TAppDetailsCpi = {
  factoryId: string;
  appVersion: string;
  shift: string;
  subject: string;
  deviceId: string;
  urId: string;
  urNm: string;
  dnrk: string;
  dnm: string;
  snm: string;
  dnr: string;
};

export type TAppDetailsWithAdditionalInfo = TAppDetails & {
  wipType: string;
  appType: string;
  dnm: string;
  dnr: string;
  shiftDn: string;
  snm: string;
  dnrk: string;
  an: string;
  urId: string;
  urNm: string;
  qtyValidation: string;
};

export type TWashingAppDetailsWithAdditionalInfo = TAppDetails & {
  wipType: string;
  appType: string;
  dnm: string;
  dnr: string;
  shiftDn: string;
  snm: string;
  dnrk: string;
  an: string;
  urId: string;
  urNm: string;
  qtyValidation: string;
  hsk: string;
  genericAppType: string;
};

export type TLotListingType =
  | 'FA_APP_LISTING'
  | 'lot_selection_listing'
  | 'lot_transaction_listing'
  | 'FA_APP_TEST_REPORT_LOT_LISTING';
export type TLotStatus = 'NOT_STARTED' | 'IN_PROGRESS' | 'PASSED' | 'FAILED';
type TFabricVariant = 'KNIT' | 'WOVEN';

type TCommonLotListingMetadata = {
  fabricVariant: TFabricVariant;
  supplierName: string;
  supplierPo: string;
  buyer: string;
  po: string;
  plm: string | null;
  style: string;
  fabricType: string;
  lotNo: string;
  fabricColor: string;
  fabricDescription: string;
  materialCode: string;
  itemCode: string;
  itemName: string;
  itemType: string;
  packed?: string;
  invoice: string | null;
};

type TLotListingMetadata = TCommonLotListingMetadata & {
  color: string;
};

type TWMSLotListingMetadata = TCommonLotListingMetadata & {
  colorCode: string;
  stockType: string;
  lotGRNStatus: LotGRNStatus;
};

type LotGRNStatus = 'STARTED' | 'NOT_STARTED';

type TLotListingInspection = {
  score: number;
  selectedRollCount: number;
  completedRollCount: number;
};

type TGRNLotListingDTO = {
  lotId: string;
  updatedAt: number;
  metadata: TWMSLotListingMetadata;
  inspection: TLotListingInspection;
  status: TLotStatus;
};

type TLotListingDTO = {
  lotId: string;
  updatedAt: number;
  metadata: TLotListingMetadata;
  inspection: TLotListingInspection;
  status: TLotStatus;
  lotGrnNumbers?: string[];
};

type TTestReportListingDTO = {
  [key: string]: string | null;
  fabricVariant: string;
  supplierName: string;
  supplierPo: string;
  buyer: string;
  plm: string | null;
  po: string;
  style: string;
  fabricType: string;
  color: string;
  fabricColor: string;
  fileKeyOpt: string | null;
  testReportStatus: 'PASS' | 'FAIL';
};

export const INSPECTION_SCORE_TYPE = {
  SQUARE_YARD_SCORE: 'SQUARE_YARD_SCORE',
  METER_YARD_SCORE: 'METER_YARD_SCORE',
  SQUARE_METER_SCORE: 'SQUARE_METER_SCORE',
  LINEAR_METER_SCORE: 'LINEAR_METER_SCORE',
} as const;

export type TInspectionScoreType = (typeof INSPECTION_SCORE_TYPE)[keyof typeof INSPECTION_SCORE_TYPE];

export type TLotInspectionStandard = {
  toBeInspectedPercentage: number;
  thresholdScore: number;
  rollThresholdScore: number | null;
  inspectionScoreType: TInspectionScoreType | null;
};

type TLotDetailInfo = {
  lotId: string;
  updatedTime: number;
  fabricVariant: TFabricVariant;
  supplierName: string;
  supplierPo: string;
  buyer: string;
  plm: string | null;
  po: string;
  style: string;
  fabricType: string;
  fabricDescription: string;
  lotNo: string;
  color: string;
  fabricColor: string;
  score: number;
  points: number;
  selectedRollCount: number;
  completedRollCount: number;
  lotStatus: TLotStatus;
  inspectionStandard: TLotInspectionStandard;
  inHouseDate: number | null;
  packed: string | null;
  invoice: string | null;
  supplierFabricInspectorReport: string | null;
  erp: string | null;
  remarks: string | null;
  fileKeyOpt: string | null;
  comment: string;
  itemName: string;
  itemCode: string;
};

export type TGRNLotDetailInfo = {
  lotId: string;
  updatedTime: number;
  fabricVariant: TFabricVariant;
  supplierName: string;
  supplierPo: string;
  buyer: string;
  plm: string | null;
  po: string;
  style: string;
  fabricType: string;
  lotNo: string;
  fabricDescription: string;
  score: number;
  points: number;
  selectedRollCount: number;
  completedRollCount: number;
  lotStatus: TLotStatus;
  inspectionStandard: TLotInspectionStandard;
  inHouseDate: number | null;
  packed: string | null;
  invoice: string | null;
  supplierFabricInspectorReport: string | null;
  erp: string | null;
  remarks: string | null;
  fileKeyOpt: string | null;
  comment: string;
  itemCode: string;
  itemName: string;
  colorCode: string;
  stockType: string;
  itemType: string;
};

export type TRollStatus = 'NOT_STARTED' | 'SELECTED' | 'IN_PROGRESS' | 'PASSED' | 'FAILED' | 'COMMERCIALLY_APPROVED';
export type TDefectLevel = 'DEFECT_LEVEL_1' | 'DEFECT_LEVEL_2' | 'DEFECT_LEVEL_3' | 'DEFECT_LEVEL_4';
export type THoleLevel = 'HOLE_LEVEL_1' | 'HOLE_LEVEL_2';

export type TYardagesArray = (number | null)[];
export type TCapturedImagesArray = (string | TBucketInfo | null)[];

export type TDefectLevelList = {
  [key in TDefectLevel]: {
    yardages: TYardagesArray;
    capturedImages: TCapturedImagesArray;
  };
};

export type TActiveDefect = {
  defectLevel: TDefectLevel;
  yardages: TYardagesArray;
  capturedImages: TCapturedImagesArray;
};

export type TActiveDefectLevelYardagesAndBucketsInfo = {
  activeDefectYardages: TYardagesArray;
  activeDefectLevelBucketsInfo: (TBucketInfo | null)[];
};

export type TFabricDefect = {
  id: string;
  defect: string;
  defectsByLevel: Record<TDefectLevel, number>;
  yardagesByLevel: Record<TDefectLevel, TYardagesArray>;
  defectImagesByYardages: Record<TDefectLevel, TCapturedImagesArray>;
};

export type TFabricDefectWithCategory = TFabricDefect & {
  category: TFabricDefectCategories;
};

export enum DefectKeyToLabelMap {
  DEFECT_LEVEL_1 = 'Upto 3 inches',
  DEFECT_LEVEL_2 = '3<6 Inches',
  DEFECT_LEVEL_3 = '6<9 inches',
  DEFECT_LEVEL_4 = '9 Inches & above',
  HOLE_LEVEL_1 = '<1 inches',
  HOLE_LEVEL_2 = '>1 Inches',
}

export type TFabricDimensionTableRowNumberPadInputs = {
  widthStart: number;
  widthMid: number;
  widthEnd: number;
  length: number;
  gsm: number | null;
  warpShrinkage: number | null;
  weftShrinkage: number | null;
  bowing: number | null;
  skewing: number | null;
};

export type TFabricDimension = TFabricDimensionTableRowNumberPadInputs & {
  csv: 'YES' | 'NO' | null;
};

export type TFabricHole = {
  holesByLevel: Record<THoleLevel, number>;
};

// If fabric hole also gets yardage
export type TFabricHoleWithYardage = {
  holesByLevel: Record<THoleLevel, (number | null)[]>;
};

export type TRollShades = {
  runningShade: string;
  sideCenter: string;
  finalShade: string;
};

export type TRollInspectionResult = {
  fabricDefects: TFabricDefect[] | TFabricDefectWithCategory[];
  fabricDimension: TFabricDimension;
  fabricHole: TFabricHole;
  score: number;
  points: number;
  shade: string;
  shades?: TRollShades;
  deviceId: string;
  appVersion: string;
  shift: string;
  subject: string;
  createdAt: number;
  updatedTime: number;
};

export type TRollInfo = {
  baseRollId: string;
  rollNo: string;
  length: number;
  weight: number;
  width: number;
  finalShade?: string;
  gsm: number;
  warpShrinkage: number | null;
  weftShrinkage: number | null;
  shade: string;
  status: TRollStatus;
  inspectionResult: TRollInspectionResult;
  rollSelectedForInspection: boolean;
  splitLength: number | null;
  siblingSplitLength: number | null;
  siblingRollNo: string | null;
  comment: string;
  grnDisplayTagId: string;
  grnNumber: string;
  grnStatus: TGRNStatus;
  grnTagId: string;
};

export type TGRNRollInfo = {
  baseRollId: string;
  rollNo: string;
  length: number;
  weight: number;
  width: number;
  gsm: number;
  warpShrinkage: number | null;
  weftShrinkage: number | null;
  shade: string;
  status: TRollStatus;
  inspectionResult: TRollInspectionResult;
  isRollSelectedForInspection: boolean;
  splitLength: number | null;
  siblingSplitLength: number | null;
  siblingRollNo: string | null;
  comment: string;
  grnStatus: TGRNStatus;
  grnNumber: string;
  grnDisplayTagId: string;
  grnTagId: string;
};

export type TGRNStatus = 'GENERATED' | 'NOT_GENERATED' | 'REJECTED';

export interface LotInfo {
  lotID: string;
  updatedTime: number;
  fabricVariant: string;
  supplierName: string;
  supplierPo: string;
  buyer: string;
  po: string;
  style: string;
  fabricType: string;
  lotNo: string;
  color: string;
  score: number;
  points: number;
  selectedRollCount: number;
  completedRollCount: null;
  lotStatus: string;
  inspectionStandard: InspectionStandard;
}

export interface InspectionStandard {
  toBeInspectedPercentage: number;
  thresholdScore: number;
}

export interface RollInfoList {
  baseRollID: string;
  rollNo: string;
  length: number;
  weight: null;
  width: number;
  gsm: null;
  status: string;
  inspectionResult: InspectionResult;
}

export interface InspectionResult {
  fabricDimension: FabricDimension;
  fabricDefects: null;
  fabricHoles: null;
  score: number;
  points: number;
  deviceID: string;
  appVersion: string;
  shift: string;
  subject: string;
  createdAt: number;
  updatedAt: number;
}

export type RollInspectionSyncPayload = {
  lotId: string;
  baseRollId: string;
  inspectionSyncType: TRollInspectionRequestType;
};

export interface FabricDimension {
  widthStart: number;
  widthMid: number;
  widthEnd: number;
  length: number;
}

type TFabricInspectionAPIStatus = 'SUCCESS' | 'FAILURE';
export type TRollInspectionRequestType = 'ROLL_INSPECTION_UPDATED' | 'ROLL_INSPECTION_COMPLETED';
type TLotCompletionStatus = 'LOT_PASSED' | 'LOT_FAILED';

type TWeightUnit = 'GM' | 'KG';

type TLengthUnit = 'CM' | 'M' | 'YARD' | 'INCH' | 'FEET';

type TBowingSkewingUnit = 'INCH' | 'DEG';

type TUoMConfig = {
  weight: TWeightUnit;
  length: TLengthUnit;
  width: TLengthUnit;
  bowing: TBowingSkewingUnit;
  skewing: TBowingSkewingUnit;
};

export type TPenaltyPointsConfig = {
  pointsByDefectLevel: Record<TDefectLevel, number>;
  pointsByHoleLevel: Record<THoleLevel, number>;
};

type TLotMetaDataConfigItem = {
  key: keyof TRESPONSE_LotDetail['lotInfo'];
  displayName: string;
};

export type ILotMetaDataConfig = TLotMetaDataConfigItem[];

// APIs

export type TRESPONSE_FabricInspectionConfig = {
  factoryId: string;
  uoMConfig: TUoMConfig;
  penaltyPointsConfig: TPenaltyPointsConfig;
  useRollLevelThreshold?: boolean;
  useFabricDefectV2: boolean;
  allowShadeUpdate?: boolean;
  showInspectionScoreTypeDropdown: boolean;
  inspectionScoreTypes: string[];
  /** Yellow,green,red tag status after roll inspection completion */
  useTagFlow?: boolean;
  /** Allow re-inspection rounds for failed lots */
  multipleInspection?: boolean;
  showFinalShadeModal: boolean;
};

export type TREQUEST_RollsCountInfo = {
  appDetails: TAppDetails;
};

export type TRESPONSE_RollsCountInfo = {
  totalInspectedRolls: number;
  totalFailedRolls: number;
  totalPassedRolls: number;
  totalInProgressRolls: number;
  totalCommerciallyApprovedRolls: number;
};

// Lot related apis
export type TREQUEST_LotListing = {
  appDetails: TAppDetails;
  filters: TFilterFabricInspection[];
  pageNo: number;
  pageSize: number;
  stockType?: TStockType;
};

export type TREQUEST_WMSLotListing = {
  appDetails: TAppDetails;
  filters: TFilterWMSFabricInspection[];
  pageNo: number;
  pageSize: number;
  itemType: TInspection_TYPE;
  stockType: TStockType;
};

export type TREQUEST_TestReportListing = TREQUEST_LotListing;

export type TREQUEST_WMSTestReportListing = TREQUEST_WMSLotListing;

export type TPaginationInfo = {
  totalPageCount: number;
  itemsPerPage?: number;
  totalItems: number;
  rangeInPage: {
    start: number;
    end: number;
  };
};

export type TRESPONSE_LotListing = {
  lotListingType: TLotListingType;
  items: TLotListingDTO[];
  selectedFilters: TFilterFabricInspection[];
  supportedFilters: TFilterFabricInspection[];
  paginationInfo: TPaginationInfo;
  headerConfig: THeaderConfig[];
};

export type TRESPONSE_WMSLotListing = {
  items: TGRNLotListingDTO[];
  selectedFilters: TFilterWMSFabricInspection[];
  supportedFilters: TFilterWMSFabricInspection[];
  paginationInfo: TPaginationInfo;
  itemType: TInspection_TYPE;
  headerConfig: THeaderConfig[];
};

export type TInspection_TYPE = 'FABRIC_INSPECTION' | 'TRIMS_INSPECTION';

export type TRESPONSE_TestReportListing = {
  lotListingType: 'FA_APP_TEST_REPORT_LOT_LISTING';
  items: TTestReportListingDTO[];
  selectedFilters: TFilterFabricInspection[];
  supportedFilters: TFilterFabricInspection[];
  paginationInfo: TPaginationInfo;
  headerConfig: THeaderConfig[];
};

export type TestReportWMSLotListingMetadata = {
  [key: string]: string | null;
  fabricVariant: TFabricVariant;
  supplierName: string;
  supplierPo: string;
  plm: string | null;
  fabricType: string;
  fabricColor: string;
  fileKeyOpt: string | null;
  testReportStatus: 'PASS' | 'FAIL';
  itemName: string;
  itemId: string;
};

export type TRESPONSE_WMSTestReportListing = {
  lotListingType: 'FA_APP_TEST_REPORT_LOT_LISTING';
  items: TestReportWMSLotListingMetadata[];
  headerConfig: THeaderConfig[];
  selectedFilters: TFilterWMSFabricInspection[];
  supportedFilters: TFilterWMSFabricInspection[];
  paginationInfo: TPaginationInfo;
};

export type TREQUEST_TestReportDelete = {
  bctxDetailsList: Omit<TTestReportListingDTO & { factoryId: string }, 'testReportFile' | 'fileKeyOpt'>[];
};

export type WMSTestReportBctxDetails = {
  factoryId: string;
  fabricVariant: TFabricVariant;
  itemType: TInspection_TYPE;
  supplierName: string;
  supplierPo: string;
  itemId: string;
  itemName: string;
  style: string;
  fabricType: string;
  colorCode: string;
  fabricColor: string;
};

export type TREQUEST_WMSTestReportDelete = {
  bctxDetailsList: WMSTestReportBctxDetails[];
};

export type TRESPONSE_TestReportDelete = {
  errorMessage: string;
  status: 'SUCCESS';
};

export type TRESPONSE_WMSTestReportDelete = {
  status: TFabricInspectionAPIStatus;
  errorMessage: string;
};

export type TREQUEST_UploadTestReport = {
  lotMetadata: {
    supplierName: string;
    supplierPo: string;
    po: string;
    plm: string;
    style: string;
    fabricType: string;
    fabricColor: string;
    color: string;
    factoryId: string;
    buyer: string;
    fabricVariant: string;
  };
  file: File;
  testReportStatus: 'PASS' | 'FAIL';
  uploadedBy: string;
};

export type TREQUEST_WMSUploadTestReport = {
  testReportBctxDetails: WMSTestReportBctxDetails;
  testReportFile: File;
  fileName: string;
  uploadedBy: string;
  testReportStatus: 'PASS' | 'FAIL';
};

export type TRESPONSE_UploadTestReport = {
  errorMessage: string;
  fileKey: string | null;
  status: 'FAILURE' | 'SUCCESS';
};

export type TRESPONSE_WMSUploadTestReport = {
  status: TFabricInspectionAPIStatus;
  errorMessage: string;
  fileKey: string | null;
};

type THeaderConfig = {
  headerKey: string;
  displayName: string;
};

export type TREQUEST_LotDetail = {
  appDetails: TAppDetails;
  lotId: string;
};

export type TREQUEST_GenerateTag = {
  appDetails: TAppDetails;
  rollIds: string[];
};

export type TRESPONSE_GenerateTag = {
  status: TFabricInspectionAPIStatus;
  errors: TFabricInspectionError[];
};

export type TRESPONSE_LotDetail = {
  lotInfo: TLotDetailInfo;
  rollInfoList: TRollInfo[];
  enableLotCompletion: boolean;
};

export type TRESPONSE_WMSLotDetail = {
  lotInfo: TGRNLotDetailInfo;
  rollInfoList: TGRNRollInfo[];
};

export type TREQUEST_StartOrUpdateLotInspection = {
  appDetails: TAppDetails;
  lotId: string;
  selectedRollIds: string[];
  inspectionStandard: TLotInspectionStandard;
};

export type TRESPONSE_StartOrUpdateLotInspection = {
  status: TFabricInspectionAPIStatus;
  lotInspectionStandard: TLotInspectionStandard;
  errors: TFabricInspectionError[];
};

export type TREQUEST_LotCompletion = {
  appDetails: TAppDetails;
  lotId: string;
  completionStatus: TLotCompletionStatus;
};

export type TRESPONSE_LotCompletion = {
  status: TFabricInspectionAPIStatus;
  errors: TFabricInspectionError[];
};

// Roll related apis

export type TREQUEST_RollInspectionSync = {
  appDetails: TAppDetails;
  rollId: string;
  fabricDimension: TFabricDimension;
  fabricDefects: TFabricDefect[];
  fabricHoles: TFabricHole;
  inspectionSyncType: TRollInspectionRequestType;
  canEditTerminalStatus: boolean;
  shade?: string;
  shades?: TRollShades;
};

export type TRESPONSE_RollInspectionSync = {
  status: TFabricInspectionAPIStatus;
  fpzScore: number;
  inspectionStandard: TLotInspectionStandard;
  errors: TFabricInspectionError[];
};

export type TRollInspectionAction =
  | 'START_INSPECTION'
  | 'RESET_INSPECTION'
  | 'ROLL_PASSED'
  | 'ROLL_FAILED'
  | 'COMMERCIALLY_APPROVED';

type TRollWithStatus = {
  rollId: string;
  rollInspectionAction: TRollInspectionAction;
};

export type TREQUEST_UpdateRollStatus = {
  appDetails: TAppDetails;
  rollList: TRollWithStatus[];
};

export type TRESPONSE_UpdateRollStatus = {
  status: TFabricInspectionAPIStatus;
  errors: TFabricInspectionError[];
};

export type TREQUEST_ChangeRollStatus = {
  factoryId: string;
  baseRollId: string;
  rollStatus: 'COMMERCIALLY_APPROVED' | 'FAILED';
};

export type TFabricInspectionError = TFabricInspectionRollLevelError | TFabricInspectionLotLevelError;
export type TFabricInspectionErrorType = 'LOT_LEVEL_ERROR' | 'ROLL_LEVEL_ERROR';
export type TFabricInspectionLotLevelError = {
  errorType: 'LOT_LEVEL_ERROR';
  errors: string[];
};
export type TFabricInspectionRollLevelError = {
  errorType: 'ROLL_LEVEL_ERROR';
  rollId: string;
  errors: string[];
};

/** Roll split request */

export type TREQUEST_RollSplitRequest = {
  factoryId: string;
  baseLotId: string;
  baseRollId: string;
  splitLength: number;
};

export type TRESPONSE_RollSplitResponse = {
  status: TFabricInspectionAPIStatus;
  splitMessage?: string;
  errors: TFabricInspectionError[];
};

/** comment on roll inspection */

export type TREQUEST_CommentForInspectedRollRequest = {
  factoryId: string;
  baseRollId: string;
  comment: string;
};

export type TRESPONSE_CommentForInspectedRollResponse = {
  status: TFabricInspectionAPIStatus;
  errors: TFabricInspectionError[];
};

export type TREQUEST_CommentForInspectedLot = {
  factoryId: string;
  baseLotId: string;
  comment: string;
};

export type TRESPONSE_CommentForInspectedLot = {
  status: TFabricInspectionAPIStatus;
  errorMessage: string;
};

export type TRESPONSE_FABRIC_INSPECTION_FILTERS_SEARCH_RES = {
  filterType: string;
  values: string[];
};

export type TREQUEST_FABRIC_INSPECTION_FILTERS_SELECTED_FILTERS = {
  key: string;
  displayName?: string;
  values: string[];
};

export type TREQUEST_getLotDetailbyGrnTag = {
  factoryId: string;
  rollId: string;
};

export type TRESPONSE_getLotDetailbyGrnTag = {
  status: TFabricInspectionAPIStatus;
  errors: TFabricInspectionError[];
  lotModel: TLotListingDTO;
  rolls: TRollInfo[];
};
